// src/components/ReturnsEntry.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  serverTimestamp,
  runTransaction, // ← NEW
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

/* ----------------------- Config ----------------------- */

const SECTIONS = [
  { id: "return-cylinder", label: "Return & Cylinder" },
  { id: "reasoning", label: "Reasons & Fault Found" },
  { id: "cyl-checklist", label: "Cylinder Checklist" },
  { id: "leak-checklist", label: "Leak Checklist" },
  { id: "parts", label: "Parts Return" },
  { id: "operators", label: "Operators" },
  { id: "notes", label: "Notes" },
  { id: "photos", label: "Photos" },
];

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "waiting_collection", label: "Waiting for collection" },
  { value: "waiting_processing", label: "Waiting to be processed" },
  { value: "in_progress", label: "In progress" },
  { value: "straggler", label: "Straggler" },
];

const ALLOW_COMPLETE_HERE = true;

// parts menu by system type (shown when "All" or "Partial")
const INDIRECT_PARTS = [
  "Inlet Group",
  "Tundish",
  "Expansion Vessel",
  "2 Port Valve",
  "Dual Stat",
  "Immersion",
];
const DIRECT_PARTS = [
  "Inlet Group",
  "Tundish",
  "Expansion Vessel",
  "Smart Immersion",
  "Immersion",
];

// leak options
const LEAK_OPTIONS = [
  { key: "verticalSeam", label: "Vertical Seam" },
  { key: "statPocket", label: "Stat Pocket" },
  { key: "topSeam", label: "Top Seam" },
  { key: "coilBoss", label: "Coil Boss" },
  { key: "bottomSeam", label: "Bottom Seam" },
  { key: "coilBody", label: "Coil Body" },
  { key: "hotDrawOff", label: "Hot Draw Off" },
  { key: "bodySheet", label: "Body Sheet" },
  { key: "secondaryReturn", label: "Secondary Return" },
  { key: "tAndPRelief", label: "T&P Relief" },
  { key: "coldFeed", label: "Cold Feed" },
  { key: "immersionBoss", label: "Immersion Boss" },
  { key: "other", label: "OTHER" },
];

const OPERATOR_FIELDS = [
  { key: "welded",      label: "Welded" },
  { key: "airTest",     label: "Air Tested" },
  { key: "hotTested",   label: "Hot Tested" },
  { key: "foamTested",  label: "Tested (Foamed)" },
];

const INITIAL_FORM = {
  returnsNoteNumber: "",
  serialNumber: "",
  sretNumber: "",
  cylinderModel: "",
  capacity: "",
  dateOfManufacture: "",
  reasonCode: "",
  reasonDefectCode: "",
  faultFoundCode: "",
  faultDefectCode: "",
  operators: { welded: "", airTest: "", hotTested: "", foamTested: "" },
  internalNotes: "",
};

const INITIAL_PARTS = {
  noPartsReturned: false,
  allPartsReturned: false,
  partialReturn: false,
  systemType: "",
  partialBaseSelection: {},
  additionalParts: [],
  notes: "",
  rmaRef: "",
};

const INITIAL_LEAKS = (() => {
  const obj = {};
  LEAK_OPTIONS.forEach(({ key }) => (obj[key] = false));
  obj.otherComments = "";
  return obj;
})();

const INITIAL_CYL = {
  cylinderInstalled: false,
  damageToCase: false,
  damageToPackaging: false,
  damageToBosses: false,
  damageToPipeworkParts: false,
  damageToFoam: false,
  stripAndTest: false,
};

/* ----------------------- Small utils ----------------------- */

const useDebouncedEffect = (fn, deps, delay) => {
  useEffect(() => {
    const t = setTimeout(fn, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const labelize = (k) =>
  k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

/* ----------------------- Returns number helpers (NEW) ----------------------- */

// Path to counters doc (same doc where you have despatchCounter)
const COUNTERS_REF = doc(db, "appData", "counters");

// Format RN as RN-000001
function formatReturnsNoteNumber(n) {
  return `RN-${String(n).padStart(6, "0")}`;
}

// Atomic increment; creates doc if missing
async function getNextReturnsNumber() {
  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(COUNTERS_REF);
    if (!snap.exists()) {
      tx.set(COUNTERS_REF, { despatchCounter: 0, returnsCounter: 1 });
      return 1;
    }
    const cur = Number(snap.data().returnsCounter || 0);
    const n = cur + 1;
    tx.update(COUNTERS_REF, { returnsCounter: n });
    return n;
  });
  return next;
}

/* ----------------------- Component ----------------------- */

export default function ReturnsEntry() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlDraftId = new URLSearchParams(search).get("draft"); // /returns/new?draft=<docId>

  // Form state
  const [form, setForm] = useState(INITIAL_FORM);
  const [parts, setParts] = useState(INITIAL_PARTS);
  const [leaks, setLeaks] = useState(INITIAL_LEAKS);
  const [cylCheck, setCylCheck] = useState(INITIAL_CYL);

  // Workflow status
  const [status, setStatus] = useState("new");
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  // Code tables
  const [reasonOptions, setReasonOptions] = useState([]); // [{code, description, requiresDefect, deprecated}]
  const [defectOptions, setDefectOptions] = useState([]); // [{code, description, deprecated}]

  // UX
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errors, setErrors] = useState({});

  // Draft
  const [draftId, setDraftId] = useState(urlDraftId || null);

  // Dirty tracking
  const snapshotKey = JSON.stringify({ form, parts, leaks, cylCheck, status });
  const [lastSavedKey, setLastSavedKey] = useState(snapshotKey);
  const dirty = snapshotKey !== lastSavedKey;

  // Highlight current section in side menu
  const observer = useRef(null);
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -70% 0px", threshold: 0 };
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id));
    }, opts);
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current?.disconnect();
  }, []);

  // Load codes once
  useEffect(() => {
    (async () => {
      const rs = await getDocs(query(collection(db, "return_reasons")));
      setReasonOptions(rs.docs.map((d) => d.data()).filter(Boolean));

      const ds = await getDocs(query(collection(db, "defect_codes")));
      setDefectOptions(ds.docs.map((d) => d.data()).filter(Boolean));
    })();
  }, []);

  // Load draft if present in URL
  useEffect(() => {
    (async () => {
      if (!urlDraftId) return;
      const snap = await getDoc(doc(db, "returns_draft", urlDraftId));
      if (!snap.exists()) {
        navigate("/returns/new", { replace: true });
        return;
      }
      const d = snap.data();
      d.form && setForm(d.form);
      d.parts && setParts(d.parts);
      d.leaks && setLeaks(d.leaks);
      d.cylCheck && setCylCheck(d.cylCheck);
      setDraftId(urlDraftId);
      setStatus(d.status || "new");
      setStatusUpdatedAt(d.statusUpdatedAt || null);
      setLastSavedKey(
        JSON.stringify({
          form: d.form || {},
          parts: d.parts || {},
          leaks: d.leaks || {},
          cylCheck: d.cylCheck || {},
          status: d.status || "new",
        })
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDraftId]);

  /* ----------------------- Validation & Progress ----------------------- */

  const getSystemList = useMemo(
    () => (systemType) =>
      systemType === "indirect"
        ? INDIRECT_PARTS
        : systemType === "direct"
        ? DIRECT_PARTS
        : [],
    []
  );

  const validate = useMemo(() => {
    return (f, p, l) => {
      const e = {};
      // Required basics (NOTE: returnsNoteNumber is auto-generated; no user input required)
      if (!f.serialNumber?.trim())
        e.serialNumber = "Serial number is required.";
      if (!f.cylinderModel?.trim())
        e.cylinderModel = "Cylinder model is required.";

      // Codes
      if (!f.reasonCode) e.reasonCode = "Pick a return reason.";
      const rMeta = reasonOptions.find((x) => x.code === f.reasonCode);
      if (rMeta?.requiresDefect && !f.reasonDefectCode)
        e.reasonDefectCode = "Pick a defect for this reason.";

      if (!f.faultFoundCode) e.faultFoundCode = "Pick a fault found code.";
      const ffMeta = reasonOptions.find((x) => x.code === f.faultFoundCode);
      if (ffMeta?.requiresDefect && !f.faultDefectCode)
        e.faultDefectCode = "Pick a defect for this fault.";

      // Parts mode
      const picked = [p.noPartsReturned, p.allPartsReturned, p.partialReturn].filter(Boolean).length;
      if (picked === 0) e.partsMode = "Select one: No parts, All parts, or Partial.";
      if (picked > 1) e.partsMode = "Only one option can be selected.";

      // SystemType required if all/partial
      if ((p.allPartsReturned || p.partialReturn) && !p.systemType)
        e.systemType = "Select Direct or Indirect.";

      // Partial needs at least one base or an additional entry
      if (p.partialReturn && p.systemType) {
        const anyBase = Object.values(p.partialBaseSelection || {}).some(Boolean);
        const anyAdditional = (p.additionalParts || []).some((x) => !!String(x).trim());
        if (!anyBase && !anyAdditional)
          e.partialItems = "Tick at least one part (or add an additional part).";
      }

      // Leak 'OTHER' must have detail
      if (l.other && !l.otherComments?.trim())
        e.leakOther = "Add details for 'OTHER'.";
      return e;
    };
  }, [reasonOptions]);

  const progress = useMemo(() => {
    let s = 0;
    if (form.returnsNoteNumber) s += 12;
    if (form.serialNumber) s += 12;
    if (form.cylinderModel) s += 12;

    if (form.reasonCode) s += 12;
    const rMeta = reasonOptions.find((x) => x.code === form.reasonCode);
    if (!rMeta?.requiresDefect || form.reasonDefectCode) s += 12;

    if (form.faultFoundCode) s += 12;
    const ffMeta = reasonOptions.find((x) => x.code === form.faultFoundCode);
    if (!ffMeta?.requiresDefect || form.faultDefectCode) s += 12;

    if (parts.noPartsReturned || parts.allPartsReturned || parts.partialReturn) s += 6;
    if ((parts.allPartsReturned || parts.partialReturn) && parts.systemType) s += 4;
    if (
      parts.partialReturn &&
      (Object.values(parts.partialBaseSelection || {}).some(Boolean) ||
        (parts.additionalParts || []).some((x) => !!String(x).trim()))
    )
      s += 3;

    if (!leaks.other || (leaks.other && leaks.otherComments)) s += 5;
    return Math.min(100, s);
  }, [form, parts, leaks, reasonOptions]);

  /* ----------------------- Draft save / autosave ----------------------- */

  const saveDraft = async (manual = false) => {
    try {
      setSaveError("");
      setIsSaving(true);

      // If this is a brand-new draft, assign a returnsNoteNumber once
      let ensuredForm = form;
      if (!draftId && !form.returnsNoteNumber) {
        const nextNum = await getNextReturnsNumber();
        ensuredForm = { ...form, returnsNoteNumber: formatReturnsNoteNumber(nextNum) };
        setForm(ensuredForm); // keep UI in sync
      }

      const payload = {
        form: ensuredForm,
        parts,
        leaks,
        cylCheck,
        status,
        // Only set statusUpdatedAt when you actually changed it in the UI
        ...(statusUpdatedAt ? { statusUpdatedAt } : {}),
        progress,
        updatedAt: serverTimestamp(),
      };

      if (!draftId) {
        const ref = await addDoc(collection(db, "returns_draft"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setDraftId(ref.id);

        // Create /ReturnsImages/{draftId} and persist storage on draft
          try {
            const fns = getFunctions(undefined, "europe-west2");
            const create = httpsCallable(fns, "spEnsureFolder");
            const { data } = await create({
              type: "returnsImage",
              subId: ref.id,
            });
            await updateDoc(doc(db, "returns_draft", ref.id), {
              storage: {
                provider: "sharepoint",
                folderId: data.folderItemId,
                folderUrl: data.folderWebUrl,
              },
            });
          } catch (e) {
            console.error("Returns folder create failed:", e); // non-fatal
          }


        const q = new URLSearchParams(search);
        q.set("draft", ref.id);
        navigate({ search: `?${q.toString()}` }, { replace: true });
      } else {
        await updateDoc(doc(db, "returns_draft", draftId), payload);
      }

      setLastSavedKey(JSON.stringify({ form: ensuredForm, parts, leaks, cylCheck, status }));
      if (manual) console.info("Draft saved.");
    } catch (e) {
      console.error(e);
      setSaveError("Could not save draft.");
    } finally {
      setIsSaving(false);
    }
  };

  // Warn if user tries to close/refresh with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (!dirty || isSubmitting) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, isSubmitting]);

  /* ----------------------- Submit ----------------------- */

  const getSystemListMemo = getSystemList; // alias for clarity

  const buildReturnedParts = (p) => {
    if (p.noPartsReturned) return [];
    const base = getSystemListMemo(p.systemType);
    if (p.allPartsReturned) {
      return [...base]; // explicit list for "All"
    }
    const chosenBase = Object.entries(p.partialBaseSelection || {})
      .filter(([, v]) => !!v)
      .map(([name]) => name);
    const extra = (p.additionalParts || []).map((x) => String(x).trim()).filter(Boolean);
    return [...chosenBase, ...extra];
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const eMap = validate(form, parts, leaks);
    setErrors(eMap);
    if (Object.keys(eMap).length) {
      const first = Object.keys(eMap)[0];
      const sectionMap = {
        serialNumber: "return-cylinder",
        cylinderModel: "return-cylinder",
        reasonCode: "reasoning",
        reasonDefectCode: "reasoning",
        faultFoundCode: "reasoning",
        faultDefectCode: "reasoning",
        partsMode: "parts",
        systemType: "parts",
        partialItems: "parts",
        leakOther: "leak-checklist",
      };
      document
        .getElementById(sectionMap[first] || "return-cylinder")
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    try {
      setIsSubmitting(true);
      if (dirty) await saveDraft(false); // flush latest

      const returnedParts = buildReturnedParts(parts);
      const finalPayload = {
        createdAt: serverTimestamp(),
        source: "pthelpdesk",
        version: 3,
        status,
        statusUpdatedAt: serverTimestamp(),
        adminNotes: "",
        notesUpdatedAt: null,
        draftRef: draftId || null,
        payload: { form, parts, leaks, cylCheck, returnedParts },
      };

      const finalRef = await addDoc(collection(db, "returns"), finalPayload);

      if (draftId) {
        await updateDoc(doc(db, "returns_draft", draftId), {
          status: "submitted",
          submittedAt: serverTimestamp(),
          finalRefId: finalRef.id,
        });
      }

      startNewReport();
    } catch (err) {
      console.error(err);
      setErrors((p) => ({ ...p, submit: "Submit failed. Try again." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ----------------------- Helpers ----------------------- */

  const startNewReport = () => {
    if (dirty && !window.confirm("Discard unsaved changes and start a new report?")) return;
    setForm(INITIAL_FORM);
    setParts(INITIAL_PARTS);
    setLeaks(INITIAL_LEAKS);
    setCylCheck(INITIAL_CYL);
    setErrors({});
    setDraftId(null);
    setStatus("new");
    setStatusUpdatedAt(null);
    setLastSavedKey(
      JSON.stringify({
        form: INITIAL_FORM,
        parts: INITIAL_PARTS,
        leaks: INITIAL_LEAKS,
        cylCheck: INITIAL_CYL,
        status: "new",
      })
    );
    navigate("/returns/new", { replace: true });
  };

  const setF = (patch) => setForm((p) => ({ ...p, ...patch }));
  const setNested = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...(ref[keys[i]] || {}) };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const togglePartsMain = (key) => {
    setParts((prev) => ({
      ...prev,
      noPartsReturned: false,
      allPartsReturned: false,
      partialReturn: false,
      [key]: !prev[key],
    }));
  };

  const setSystemType = (type) => {
    setParts((prev) => {
      const baseList = getSystemListMemo(type);
      const baseMap = Object.fromEntries(baseList.map((n) => [n, false]));
      return {
        ...prev,
        systemType: type,
        partialBaseSelection: baseMap,
      };
    });
  };

  const togglePartialBase = (name) => {
    setParts((prev) => ({
      ...prev,
      partialBaseSelection: {
        ...(prev.partialBaseSelection || {}),
        [name]: !prev?.partialBaseSelection?.[name],
      },
    }));
  };

  const addAdditionalPart = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    setParts((prev) => ({
      ...prev,
      additionalParts: [...(prev.additionalParts || []), clean],
    }));
  };

  const removeAdditionalPart = (idx) => {
    setParts((prev) => {
      const arr = [...(prev.additionalParts || [])];
      arr.splice(idx, 1);
      return { ...prev, additionalParts: arr };
    });
  };

  const systemParts = getSystemListMemo(parts.systemType);

  const getReasonMeta = (code) => reasonOptions.find((r) => r.code === code);
  const reasonRequiresDefect = !!getReasonMeta(form.reasonCode)?.requiresDefect;
  const faultRequiresDefect = !!getReasonMeta(form.faultFoundCode)?.requiresDefect;

  /* ----------------------- Render ----------------------- */

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Returns — Cylinder</h1>

        {/* Status selector */}
        <div className="ml-4">
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              setStatusUpdatedAt(new Date().toISOString());
            }}
            className="text-sm rounded-md border border-gray-300 px-3 py-2"
            title="Workflow status"
          >
            {STATUS_OPTIONS.filter(o => ALLOW_COMPLETE_HERE ? true : o.value !== "complete")
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))
            }
          </select>
        </div>

        <span className="ml-auto text-sm text-gray-500">
          {isSaving
            ? "Saving draft…"
            : dirty
            ? "Unsaved changes"
            : draftId
            ? "All changes saved"
            : "Nothing saved yet"}
        </span>

        {saveError && <span className="text-sm text-red-600">{saveError}</span>}
        <button
          type="button"
          onClick={() => saveDraft(true)}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Save draft
        </button>
        <button
          type="button"
          onClick={startNewReport}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          title="Start a new blank report without submitting this one"
        >
          New report
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-4 h-max bg-white border border-gray-200 rounded-xl p-3">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() =>
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
                }
                className={`w-full text-left px-3 py-2 rounded-md transition ${
                  activeId === id
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-gray-100 text-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="mt-4 p-3 bg-gray-50 border border-dashed rounded-md text-xs text-gray-600">
            <div>
              <b>Draft:</b> {draftId ? draftId.slice(-8) : "none"}
            </div>
            <div>
              <b>Progress:</b> {Math.round(progress)}%
            </div>
          </div>
        </aside>

        {/* Main form */}
        <form onSubmit={onSubmit} className="space-y-10">
          {/* Return & Cylinder */}
          <Section id="return-cylinder" title="Return & Cylinder">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField
                label="Returns Note Number"
                value={form.returnsNoteNumber}
                onChange={() => {}}
                readOnly
              />
              <TextField
                label="Serial Number"
                value={form.serialNumber}
                onChange={(v) => setF({ serialNumber: v })}
                error={errors.serialNumber}
              />
              <TextField
                label="SRET Number"
                value={form.sretNumber}
                onChange={(v) => setF({ sretNumber: v })}
              />
              <TextField
                label="Cylinder Model"
                value={form.cylinderModel}
                onChange={(v) => setF({ cylinderModel: v })}
                error={errors.cylinderModel}
              />
              <TextField
                label="Capacity"
                value={form.capacity}
                onChange={(v) => setF({ capacity: v })}
                placeholder="e.g., 200L"
              />
              <TextField
                label="Date of Manufacture"
                type="date"
                value={form.dateOfManufacture}
                onChange={(v) => setF({ dateOfManufacture: v })}
                placeholder="YYYY-MM-DD"
              />
            </div>
          </Section>

          {/* Reasons & Fault Found */}
          <Section id="reasoning" title="Reasons & Fault Found (coded)">
            <div className="grid sm:grid-cols-2 gap-4">
              <CodeSelect
                label="Return Reason"
                value={form.reasonCode}
                onChange={(code) => setF({ reasonCode: code, reasonDefectCode: "" })}
                options={reasonOptions.filter((r) => !r.deprecated)}
                getOptionLabel={(r) => `${r.code} — ${r.description}`}
                getOptionValue={(r) => r.code}
                placeholder="Search code or description…"
                error={errors.reasonCode}
              />
              {reasonRequiresDefect && (
                <CodeSelect
                  label="Reason Defect"
                  value={form.reasonDefectCode}
                  onChange={(code) => setF({ reasonDefectCode: code })}
                  options={defectOptions.filter((d) => !d.deprecated)}
                  getOptionLabel={(d) => `${d.code} — ${d.description}`}
                  getOptionValue={(d) => d.code}
                  placeholder="Search defect…"
                  error={errors.reasonDefectCode}
                />
              )}

              <CodeSelect
                label="Fault Found"
                value={form.faultFoundCode}
                onChange={(code) => setF({ faultFoundCode: code, faultDefectCode: "" })}
                options={reasonOptions.filter((r) => !r.deprecated)}
                getOptionLabel={(r) => `${r.code} — ${r.description}`}
                getOptionValue={(r) => r.code}
                placeholder="Search code or description…"
                error={errors.faultFoundCode}
              />
              {faultRequiresDefect && (
                <CodeSelect
                  label="Fault Defect"
                  value={form.faultDefectCode}
                  onChange={(code) => setF({ faultDefectCode: code })}
                  options={defectOptions.filter((d) => !d.deprecated)}
                  getOptionLabel={(d) => `${d.code} — ${d.description}`}
                  getOptionValue={(d) => d.code}
                  placeholder="Search defect…"
                  error={errors.faultDefectCode}
                />
              )}
            </div>
          </Section>

          {/* Cylinder Checklist */}
          <Section id="cyl-checklist" title="Cylinder Checklist">
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "cylinderInstalled",
                "damageToCase",
                "damageToPackaging",
                "damageToBosses",
                "damageToPipeworkParts",
                "damageToFoam",
                "stripAndTest",
              ].map((k) => (
                <CheckRow
                  key={k}
                  label={labelize(k)}
                  checked={!!cylCheck[k]}
                  onChange={() =>
                    setCylCheck((p) => ({
                      ...p,
                      [k]: !p[k],
                    }))
                  }
                />
              ))}
            </div>
          </Section>

          {/* Leak Checklist */}
          <Section id="leak-checklist" title="Leak Checklist">
            <div className="grid sm:grid-cols-2 gap-3">
              {LEAK_OPTIONS.map(({ key, label }) =>
                key !== "other" ? (
                  <CheckRow
                    key={key}
                    label={label}
                    checked={!!leaks[key]}
                    onChange={() => setLeaks((p) => ({ ...p, [key]: !p[key] }))}
                  />
                ) : null
              )}
              {/* OTHER toggle */}
              <CheckRow
                label="OTHER"
                checked={!!leaks.other}
                onChange={() => setLeaks((p) => ({ ...p, other: !p.other }))}
              />
            </div>
            {leaks.other && (
              <div className="mt-4">
                <TextField
                  label="Other Comments"
                  value={leaks.otherComments}
                  onChange={(v) => setLeaks((p) => ({ ...p, otherComments: v }))}
                  error={errors.leakOther}
                />
              </div>
            )}
          </Section>

          {/* Parts Return */}
          <Section id="parts" title="Parts Return">
            <div className="flex flex-wrap gap-3 mb-4">
              <TogglePill
                label="No parts returned"
                active={parts.noPartsReturned}
                onClick={() => togglePartsMain("noPartsReturned")}
              />
              <TogglePill
                label="All parts returned"
                active={parts.allPartsReturned}
                onClick={() => togglePartsMain("allPartsReturned")}
              />
              <TogglePill
                label="Partial return"
                active={parts.partialReturn}
                onClick={() => togglePartsMain("partialReturn")}
              />
            </div>
            {errors.partsMode && <FieldError text={errors.partsMode} />}

            {(parts.allPartsReturned || parts.partialReturn) && (
              <>
                {/* System type selection */}
                <div className="flex gap-3 items-center mb-3">
                  <span className="text-sm text-gray-700">System type:</span>
                  <TogglePill
                    label="Indirect"
                    active={parts.systemType === "indirect"}
                    onClick={() => setSystemType("indirect")}
                  />
                  <TogglePill
                    label="Direct"
                    active={parts.systemType === "direct"}
                    onClick={() => setSystemType("direct")}
                  />
                </div>
                {errors.systemType && <FieldError text={errors.systemType} />}

                {/* "All" – show list explicitly */}
                {parts.allPartsReturned && parts.systemType && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 font-medium mb-1">Items included:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-800">
                      {systemParts.map((pname) => (
                        <li key={pname}>{pname}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Partial selection grid */}
                {parts.partialReturn && parts.systemType && (
                  <>
                    <p className="text-sm text-gray-700 font-medium mb-1">Select returned parts:</p>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-2">
                      {systemParts.map((pname) => (
                        <CheckRow
                          key={pname}
                          label={pname}
                          checked={!!parts.partialBaseSelection?.[pname]}
                          onChange={() => togglePartialBase(pname)}
                        />
                      ))}
                    </div>
                    {/* Additional parts */}
                    <AdditionalPartsEditor
                      items={parts.additionalParts || []}
                      onAdd={addAdditionalPart}
                      onRemove={removeAdditionalPart}
                    />
                    {errors.partialItems && <FieldError text={errors.partialItems} />}
                  </>
                )}
              </>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <TextField
                label="Parts Notes (optional)"
                value={parts.notes}
                onChange={(v) => setParts((p) => ({ ...p, notes: v }))}
              />
              <TextField
                label="RMA / Returns Ref (optional)"
                value={parts.rmaRef}
                onChange={(v) => setParts((p) => ({ ...p, rmaRef: v }))}
              />
            </div>
          </Section>

          {/* Operators */}
          <Section id="operators" title="Operators (Initials)">
            <div className="grid sm:grid-cols-2 gap-4">
              {OPERATOR_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  value={form.operators[key] || ""}
                  onChange={(v) => setNested(`operators.${key}`, v)}
                  placeholder="e.g., PT"
                />
              ))}
            </div>
          </Section>

          {/* Notes */}
          <Section id="notes" title="Notes">
            <TextArea
              label="Internal Notes"
              value={form.internalNotes}
              onChange={(v) => setF({ internalNotes: v })}
              placeholder="Anything extra the workshop / tech team should know…"
            />
          </Section>

          {/* Photos */}
          <ReturnsPhotos draftId={draftId} />

          {/* Sticky submit */}
          <div className="h-16" />
          <div className="fixed bottom-4 left-0 right-0 px-4">
            <div className="max-w-7xl mx-auto bg-white border border-gray-200 shadow-xl rounded-xl p-3 flex items-center gap-3">
              <span className="text-sm text-gray-600">
                Draft: {draftId ? draftId.slice(-8) : "none"} · Status {STATUS_OPTIONS.find(s => s.value === status)?.label || status} · Progress {Math.round(progress)}%
              </span>
              <button
                type="button"
                onClick={() => saveDraft(true)}
                className="ml-auto px-4 py-3 rounded-md border border-gray-300 hover:bg-gray-50"
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : "Save draft"}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-md text-white transition ${
                  isSubmitting ? "bg-indigo-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isSubmitting ? "Submitting…" : "Submit Return"}
              </button>
            </div>
          </div>
          {errors.submit && <FieldError text={errors.submit} />}
        </form>
      </div>
    </div>
  );
}

/* ---------------- Photos for Returns ---------------- */
function ReturnsPhotos({ draftId }) {
  const [folderId, setFolderId] = useState(null);
  const [folderUrl, setFolderUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});
  const [err, setErr] = useState("");

  const fns = useMemo(() => getFunctions(undefined, "europe-west2"), []);

  // NEW: refs for separate camera/gallery pickers
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (!draftId) return;
    const ref = doc(db, "returns_draft", draftId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const s = snap.data()?.storage;
      if (s?.folderId) {
        setFolderId(s.folderId);
        setFolderUrl(s.folderUrl || null);
      }
    });
    return () => unsub();
  }, [draftId]);

  const refreshImages = useMemo(
    () => async () => {
      if (!folderId) return;
      setLoading(true);
      setErr("");
      try {
        const list = httpsCallable(fns, "spListImages");
        const { data } = await list({ folderItemId: folderId });
        setImages(data.images || []);
      } catch (e) {
        console.error(e);
        setErr(e.message || "Failed to load images");
      } finally {
        setLoading(false);
      }
    },
    [fns, folderId]
  );

  useEffect(() => {
    refreshImages();
  }, [refreshImages]);

  async function getUploadUrl(fileName) {
    const create = httpsCallable(fns, "spCreateUploadSession");
    const { data } = await create({ folderItemId: folderId, fileName });
    return data.uploadUrl;
  }

  async function putWithProgress(uploadUrl, file, key) {
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct } }));
      };
      xhr.onload = () => {
        setProgress((p) => ({ ...p, [key]: { name: file.name, pct: 100 } }));
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Length", String(file.size));
      xhr.setRequestHeader("Content-Range", `bytes 0-${file.size - 1}/${file.size}`);
      xhr.send(file);
    });
  }

  const onPick = async (files) => {
    if (!folderId) return;
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true);
    try {
      for (const f of arr) {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        const url = await getUploadUrl(f.name);
        await putWithProgress(url, f, key);
      }
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Upload failed");
    } finally {
      setBusy(false);
      // clear inputs so the same file can be picked twice if needed
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const onDelete = async (itemId) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      const del = httpsCallable(fns, "spDeleteDriveItem");
      await del({ itemId });
      await refreshImages();
    } catch (e) {
      console.error(e);
      alert(e.message || "Delete failed");
    }
  };

      const createFolderNow = async () => {
      if (!draftId) return;
      try {
        setBusy(true);
        const create = httpsCallable(fns, "spEnsureFolder");
        const { data } = await create({
          type: "returnsImage",
          subId: draftId,
        });
        await updateDoc(doc(db, "returns_draft", draftId), {
          storage: { provider: "sharepoint", folderId: data.folderItemId, folderUrl: data.folderWebUrl },
        });
      } catch (e) {
        console.error(e);
        alert(e.message || "Could not create folder");
      } finally {
        setBusy(false);
      }
    };


  return (
    <Section id="photos" title="Photos (optional)">
      {!draftId ? (
        <p className="text-sm text-gray-500">Save a draft first to attach photos.</p>
      ) : !folderId ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600">Folder not ready yet.</p>
          <button
            type="button"
            onClick={createFolderNow}
            disabled={busy}
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          >
            {busy ? "Creating…" : "Create folder"}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-sm text-gray-700 mr-1">Add photos:</span>

            {/* Take photo (opens camera) */}
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => cameraInputRef.current?.click()}
              disabled={busy}
            >
              📷 Take photo
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {/* Upload from gallery (opens picker) */}
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
              onClick={() => galleryInputRef.current?.click()}
              disabled={busy}
            >
              🖼️ Upload from gallery
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />

            {folderUrl && (
              <a
                className="ml-auto text-sm underline text-indigo-700"
                href={folderUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open folder
              </a>
            )}
            <button
              type="button"
              onClick={refreshImages}
              disabled={loading || busy}
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {Object.keys(progress).length > 0 && (
            <div className="mb-3 grid gap-2">
              {Object.entries(progress).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <div className="flex justify-between">
                    <span className="truncate mr-2">{v.name}</span>
                    <span>{v.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-indigo-600 rounded" style={{ width: `${v.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {err && <p className="text-sm text-red-600 mb-2">{err}</p>}

          {loading ? (
            <p className="text-sm text-gray-500">Loading images…</p>
          ) : images.length === 0 ? (
            <p className="text-sm text-gray-500">No images uploaded.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img) => (
                <div key={img.id} className="border rounded overflow-hidden">
                  <a href={img.webUrl} target="_blank" rel="noreferrer">
                    <img
                      src={img.thumbUrl || img.webUrl}
                      alt={img.name}
                      className="w-full h-36 object-cover"
                      loading="lazy"
                    />
                  </a>
                  <div className="p-2 text-xs flex items-center justify-between gap-2">
                    <div className="truncate" title={img.name}>{img.name}</div>
                    <button
                      type="button"
                      onClick={() => onDelete(img.id)}
                      className="text-red-600 hover:underline"
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}


/* ----------------------- UI Bits ----------------------- */

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, placeholder = "", error, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        } ${readOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
        value={value || ""}
        onChange={(e) => { if (!readOnly) onChange(e.target.value); }}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder = "", error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={4}
        className={`w-full rounded-md border px-3 py-2 outline-none transition ${
          error ? "border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-300 focus:ring-2 focus:ring-indigo-200"
        }`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && <FieldError text={error} />}
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
      <input type="checkbox" className="h-4 w-4" checked={!!checked} onChange={onChange} />
      <span className="text-sm text-gray-800">{label}</span>
    </label>
  );
}

function TogglePill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full border transition ${
        active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );
}

function FieldError({ text }) {
  return <p className="mt-1 text-sm text-red-600">{text}</p>;
}

/* Searchable, dependency-free select */
function CodeSelect({
  label,
  value,
  onChange,
  options,
  getOptionLabel,
  getOptionValue,
  placeholder,
  error,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => getOptionLabel(o).toLowerCase().includes(term));
  }, [q, options, getOptionLabel]);

  const selected = options.find((o) => getOptionValue(o) === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        className={`w-full rounded-md border px-3 py-2 flex items-center justify-between cursor-pointer ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm text-gray-800">
          {selected ? getOptionLabel(selected) : <span className="text-gray-400">Select…</span>}
        </span>
        <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20">
          <path d="M5 7l5 5 5-5H5z" />
        </svg>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2">
            <input
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder={placeholder || "Search…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-56 overflow-auto">
            {list.length === 0 && (
              <div className="p-3 text-sm text-gray-500">No matches</div>
            )}
            {list.map((o) => {
              const val = getOptionValue(o);
              return (
                <button
                  key={val}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    val === value ? "bg-gray-100" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(val);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  {getOptionLabel(o)}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <FieldError text={error} />}
    </div>
  );
}

function AdditionalPartsEditor({ items, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="mt-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">Additional parts</label>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Type a part name (e.g., PRV, Check Valve)…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd(draft);
              setDraft("");
            }
          }}
        />
        <button
          type="button"
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
          onClick={() => {
            onAdd(draft);
            setDraft("");
          }}
        >
          Add
        </button>
      </div>
      {items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((name, idx) => (
            <span
              key={`${name}-${idx}`}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm"
            >
              {name}
              <button
                type="button"
                className="text-red-600"
                onClick={() => onRemove(idx)}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
